import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ConfettiBurst from "@/components/ConfettiBurst";
import Quiz from "@/components/Quiz";
import LetterCard from "@/components/LetterCard";
import WordCard from "@/components/WordCard";
import { fetchDailyPlan, invalidateDailyPlan } from "@/lib/dailyPlanStore";
import AlphabetLearnScreen from "@/components/AlphabetLearnScreen";
import DailyPlanIntro from "@/components/DailyPlanIntro";
import DailyPlanHeader from "@/components/DailyPlanHeader";
import InterExerciseRecap from "@/components/InterExerciseRecap";
import { buildVocabQuestions } from "@/lib/vocabQuiz";
import { makeStrictSequence } from "@/lib/quizSequence";
import {
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Sparkles,
  Home,
  Target,
  BookOpen,
  ListChecks,
  RotateCcw,
  RefreshCw,
  Repeat,
  Coins,
  Lock,
  Type,
  Languages,
  PenLine,
  AlertTriangle,
} from "lucide-react";
import { t } from "@/i18n";

/* ---------------------------------------------------------------------------
 * Direction du texte
 * L'ancienne détection basculait toute la phrase en RTL dès qu'un seul
 * caractère arabe apparaissait, ce qui cassait les consignes mixtes du type
 * « Traduis كتاب en français ». On respecte d'abord la donnée serveur, et on
 * ne bascule que si l'arabe est vraiment majoritaire.
 * ------------------------------------------------------------------------- */
const arabicShare = (s) => {
  const str = String(s || "");
  const letters = str.replace(/[\s\d\p{P}]/gu, "");
  if (!letters.length) return 0;
  const arabic = letters.match(/[\u0600-\u06FF]/g) || [];
  return arabic.length / letters.length;
};
const isArabic = (s) => arabicShare(s) > 0.5;
const dirOf = (s, forced) => ((forced ?? isArabic(s)) ? "rtl" : "ltr");

const SUBTYPE_META = {
  alphabet: { icon: Type, label: "Alphabet" },
  vocab: { icon: Languages, label: "Vocabulaire" },
  conjugation: { icon: Repeat, label: "Conjugaison" },
  roots: { icon: Target, label: "Racines" },
  sentence: { icon: PenLine, label: "Construction de phrases" },
  course: { icon: BookOpen, label: "Questions de cours" },
  review: { icon: RefreshCw, label: "Révision" },
  errors: { icon: AlertTriangle, label: "Erreurs" },
};

/* =========================================================================
 * Carte de question — un seul rendu pour toute l'application
 * ======================================================================= */
function QuestionCard({ question, index, total, onAnswered, onNext }) {
  const [pick, setPick] = useState(null);
  const [validated, setValidated] = useState(false);
  const q = question;
  const correct = pick === q.answer;

  useEffect(() => {
    setPick(null);
    setValidated(false);
  }, [q.key, index]);

  const submit = () => {
    if (pick === null || validated) return;
    setValidated(true);
    onAnswered(pick === q.answer);
  };

  const promptRtl = q.arabic_prompt ?? isArabic(q.prompt);
  const optionsRtl = q.arabic_options ?? null;

  return (
    <div
      className={`rounded-3xl border-2 p-6 md:p-8 shadow-sm bg-card transition-colors ${
        validated
          ? correct
            ? "border-green-500/50 bg-green-50/30 dark:bg-green-950/10"
            : "border-red-500/50 bg-red-50/30 dark:bg-red-950/10"
          : "border-border"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm font-bold text-muted-foreground flex items-center gap-2">
          <ListChecks size={18} /> {t("exerciseblock.question")} {index + 1} sur {total}
        </div>
        {q.difficulty ? (
          <span className="text-xs px-3 py-1 rounded-full bg-secondary font-bold text-secondary-foreground">
            {t("dailyplansession.difficulte")} {q.difficulty}
          </span>
        ) : null}
      </div>

      {q.char && (
        <div className="mb-6 text-center">
          <span className="arabic text-7xl font-bold" dir="rtl">
            {q.char}
          </span>
        </div>
      )}

      <div
        className={`mb-8 font-semibold text-foreground ${
          promptRtl ? "arabic text-3xl leading-relaxed" : "text-xl"
        }`}
        dir={promptRtl ? "rtl" : "ltr"}
      >
        {q.prompt}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {(q.options || []).map((opt, oi) => {
          const isPicked = pick === oi;
          const isRight = oi === q.answer;
          let cls =
            "border-2 border-border hover:border-primary/50 hover:bg-secondary/50 active:scale-[0.98]";
          if (validated) {
            if (isRight)
              cls =
                "border-2 border-green-500 bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300 font-bold scale-[1.02] shadow-sm";
            else if (isPicked)
              cls =
                "border-2 border-red-500 bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 font-bold";
            else cls = "border-2 border-border opacity-40 grayscale";
          } else if (isPicked) {
            cls =
              "border-2 border-primary bg-primary/10 text-primary font-bold scale-[1.02] shadow-sm";
          }
          const rtl = optionsRtl ?? isArabic(opt);
          return (
            <button
              key={oi}
              disabled={validated}
              onClick={() => setPick(oi)}
              className={`px-5 py-4 rounded-2xl text-left transition-all duration-200 text-base ${cls} ${
                rtl ? "arabic text-2xl text-center" : ""
              }`}
              dir={rtl ? "rtl" : "ltr"}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {!validated ? (
        <button
          onClick={submit}
          disabled={pick === null}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg disabled:opacity-40 active:scale-[0.98] transition-all shadow-md"
        >
          {t("exerciseblock.valider")}
        </button>
      ) : (
        <div>
          <div
            className={`p-5 rounded-2xl mb-6 flex flex-col gap-2 ${
              correct
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
            }`}
          >
            <div className="font-black text-lg flex items-center gap-2">
              {correct ? (
                <>
                  <CheckCircle2 size={24} /> {t("dailyplansession.bonne_reponse")}
                </>
              ) : (
                <>
                  <XCircle size={24} /> {t("dailyplansession.rate")}
                </>
              )}
            </div>
            {!correct && (
              <div className="text-sm opacity-90">
                {t("dailyplansession.la_bonne_reponse_etait")}{" "}
                <span className="font-bold">{(q.options || [])[q.answer]}</span>
              </div>
            )}
            {q.explain && (
              <div className="mt-1 text-sm bg-white/40 dark:bg-black/20 p-3 rounded-xl">
                {q.explain}
              </div>
            )}
          </div>
          <button
            onClick={onNext}
            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all shadow-md"
          >
            {t("exerciseblock.continuer")} <ArrowRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Construction de phrases — placement au doigt, sans dépendance drag & drop
 * ======================================================================= */
function SentenceCard({ sentence, index, total, onAnswered, onNext }) {
  const [slots, setSlots] = useState({});
  const [validated, setValidated] = useState(false);
  const blanks = useMemo(
    () => (sentence.tokens || []).filter((t) => t.blank).map((t) => t.index),
    [sentence],
  );

  useEffect(() => {
    setSlots({});
    setValidated(false);
  }, [sentence.key]);

  const placed = Object.values(slots).map((c) => c.id);
  const available = (sentence.bank || []).filter((c) => !placed.includes(c.id));
  const nextEmpty = blanks.find((b) => !slots[b]);

  const place = (chip) => {
    if (validated || nextEmpty === undefined) return;
    setSlots((s) => ({ ...s, [nextEmpty]: chip }));
  };
  const clearSlot = (idx) => {
    if (validated) return;
    setSlots((s) => {
      const next = { ...s };
      delete next[idx];
      return next;
    });
  };

  const allFilled = blanks.every((b) => slots[b]);
  const isCorrect = blanks.every(
    (b) => slots[b] && slots[b].correct_index === b,
  );

  const submit = () => {
    if (!allFilled || validated) return;
    setValidated(true);
    onAnswered(isCorrect);
  };

  return (
    <div
      className={`rounded-3xl border-2 p-6 md:p-8 shadow-sm bg-card transition-colors ${
        validated
          ? isCorrect
            ? "border-green-500/50 bg-green-50/30 dark:bg-green-950/10"
            : "border-red-500/50 bg-red-50/30 dark:bg-red-950/10"
          : "border-border"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm font-bold text-muted-foreground flex items-center gap-2">
          <PenLine size={18} /> {t("dailyplansession.phrase")} {index + 1} sur {total}
        </div>
      </div>

      <p className="text-muted-foreground mb-6">{sentence.translation_fr}</p>

      <div
        className="flex flex-wrap-reverse justify-end gap-2 mb-8 p-4 rounded-2xl bg-secondary/30 min-h-[80px]"
        dir="rtl"
      >
        {(sentence.tokens || []).map((tok) => {
          if (!tok.blank) {
            return (
              <span key={tok.index} className="arabic text-2xl py-2 px-1">
                {tok.text}
              </span>
            );
          }
          const chip = slots[tok.index];
          const right = validated && chip && chip.correct_index === tok.index;
          return (
            <button
              key={tok.index}
              onClick={() => clearSlot(tok.index)}
              className={`arabic text-2xl min-w-[90px] px-3 py-2 rounded-xl border-2 border-dashed transition ${
                validated
                  ? right
                    ? "border-green-500 bg-green-100 dark:bg-green-500/20"
                    : "border-red-500 bg-red-100 dark:bg-red-500/20"
                  : chip
                    ? "border-primary bg-primary/10 border-solid"
                    : "border-border bg-background"
              }`}
            >
              {chip ? chip.text : "…"}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 justify-center mb-8">
        {available.map((chip) => (
          <button
            key={chip.id}
            onClick={() => place(chip)}
            disabled={validated}
            className="inline-flex flex-col items-center rounded-xl border-2 bg-card px-4 py-2 shadow-sm hover:border-primary active:scale-95 transition disabled:opacity-40"
          >
            <span className="arabic text-xl font-bold" dir="rtl">
              {chip.text}
            </span>
            {chip.gloss && (
              <span className="text-[11px] text-muted-foreground">
                {chip.gloss}
              </span>
            )}
          </button>
        ))}
      </div>

      {!validated ? (
        <button
          onClick={submit}
          disabled={!allFilled}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg disabled:opacity-40 active:scale-[0.98] transition-all shadow-md"
        >
          {t("exerciseblock.valider")}
        </button>
      ) : (
        <button
          onClick={onNext}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-md"
        >
          {t("exerciseblock.continuer")} <ArrowRight size={20} />
        </button>
      )}
    </div>
  );
}

/* =========================================================================
 * Bloc leçon — cours, puis questions une par une
 * ======================================================================= */
function LessonBlock({ item, onAnswer, onAdvance, onReset }) {
  const b = item.block || {};
  const questions = b.questions || [];
  const required = item.required || 3;
  const already = item.validations || 0;
  const passPercent = item.pass_percent || 80;

  const [phase, setPhase] = useState("reading");
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);

  const record = (correct) => {
    const q = questions[qIdx];
    setAnswers((prev) => [...prev, { correct }]);
    onAnswer({
      item_key: q.key,
      item_type: "lesson_quiz",
      ref_id: b.id,
      prompt: q.prompt,
      correct,
      skill: q.skill || b.skill || "grammar",
      difficulty: q.difficulty || 2,
      options: q.options || [],
      answer: q.answer ?? 0,
      reward_mode: "lesson_pending",
    });
  };

  const next = () => {
    if (qIdx + 1 < questions.length) setQIdx(qIdx + 1);
    else setPhase("done");
  };

  const restart = (from) => {
    onReset && onReset(questions.map((q) => q.key));
    setPhase(from);
    setQIdx(0);
    setAnswers([]);
  };

  if (phase === "reading") {
    return (
      <div className="rounded-3xl border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-4 mb-6 pb-4 border-b">
          <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-md">
            <BookOpen size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm uppercase font-extrabold text-primary mb-1 tracking-wider">
              {item.mode === "rework" ? t("dailyplansession.a_retravailler") : t("dailyplansession.lecon")} · {b.skill}
            </div>
            <div className="font-head text-2xl font-bold truncate text-foreground">
              {b.title}
            </div>
            {b.objective && (
              <div className="text-sm text-muted-foreground truncate mt-1">
                {b.objective}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 rounded-2xl bg-secondary/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold">
              {t("dailyplansession.validation")} {Math.min(already + 1, required)} sur {required}
            </span>
            <span className="text-xs text-muted-foreground">
              {passPercent} {t("dailyplansession.requis")}{item.reward || 10} 🪙
            </span>
          </div>
          <div className="flex gap-1.5">
            {Array.from({ length: required }).map((_, i) => (
              <div
                key={i}
                className={`h-2.5 flex-1 rounded-full ${
                  i < already ? "bg-green-500" : "bg-secondary-foreground/15"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t("dailyplansession.une_lecon_est_acquise_apres")} {required} {t("dailyplansession.reussites_une_par_jour_maximum")}
          </p>
        </div>

        {(b.blocks || []).length > 0 && (
          <div className="space-y-4 mb-8 text-base">
            {(b.blocks || []).map((bl, i) => {
              if (bl.type === "example")
                return (
                  <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center p-4 rounded-2xl bg-secondary/40 border border-secondary"
                  >
                    <div
                      className="font-head text-2xl arabic text-right text-foreground"
                      dir="rtl"
                    >
                      {bl.ar}
                    </div>
                    <div className="italic text-muted-foreground text-center text-sm">
                      {bl.translit}
                    </div>
                    <div className="text-left font-medium text-foreground">
                      {bl.fr}
                    </div>
                  </div>
                );
              if (bl.type === "concept")
                return (
                  <div
                    key={i}
                    className="rounded-2xl bg-primary/10 border border-primary/20 p-5 font-medium text-primary"
                  >
                    💡 {bl.value}
                  </div>
                );
              if (bl.type === "tip")
                return (
                  <div
                    key={i}
                    className="rounded-2xl bg-ocre/10 border border-ocre/20 text-ocre p-5 font-medium"
                  >
                    ✨ {bl.value}
                  </div>
                );
              return (
                <p key={i} className="leading-relaxed text-foreground/90">
                  {bl.value}
                </p>
              );
            })}
          </div>
        )}

        <button
          onClick={() => (questions.length ? setPhase("quiz") : onAdvance())}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all shadow-md"
        >
          {questions.length ? t("dailyplansession.passer_aux_questions") : t("dailyplanintro.continuer")}{" "}
          <ArrowRight size={20} />
        </button>
      </div>
    );
  }

  if (phase === "done") {
    const correctCount = answers.filter((a) => a.correct).length;
    const score = Math.round(
      (correctCount / Math.max(1, answers.length)) * 100,
    );
    const passed = score >= passPercent;
    return (
      <div
        className={`rounded-3xl border-2 p-8 shadow-sm ${
          passed
            ? "border-emerald-500/30 bg-card"
            : "border-red-500/40 bg-red-500/5"
        }`}
      >
        <div className="text-center">
          {passed ? (
            <CheckCircle2 size={64} className="mx-auto mb-5 text-green-500" />
          ) : (
            <XCircle size={64} className="mx-auto mb-5 text-red-600" />
          )}
          <h2 className="font-head text-3xl font-bold mb-2 text-foreground">
            {passed ? t("dailyplansession.reussi") : t("dailyplansession.pas_encore")}
          </h2>
          <div
            className={`text-5xl font-head font-extrabold mb-2 ${
              passed
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {score}%
          </div>
          <p className="text-muted-foreground mb-6">
            {correctCount}/{answers.length} {t("interexerciserecap.bonnes_reponses")}
          </p>
          <p
            className={`text-sm font-semibold mb-8 ${passed ? "text-green-600" : "text-red-600"}`}
          >
            {passed
              ? `Validation ${Math.min(already + 1, required)}/${required} · +${item.reward || 10} 🪙 à la fin de la séance`
              : `Il faut ${passPercent} % pour valider. Rien n'est perdu : tu retentes demain.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => restart("quiz")}
              className="px-5 py-3 rounded-xl border font-semibold hover:bg-secondary transition flex items-center justify-center gap-2"
            >
              <RotateCcw size={17} /> {t("dailyplansession.refaire_le_quiz")}
            </button>
            <button
              onClick={() => restart("reading")}
              className="px-5 py-3 rounded-xl border font-semibold hover:bg-secondary transition flex items-center justify-center gap-2"
            >
              <BookOpen size={17} /> {t("dailyplansession.relire_la_lecon")}
            </button>
            <button
              onClick={onAdvance}
              className="px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              {t("exerciseblock.continuer")} <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <QuestionCard
      question={questions[qIdx]}
      index={qIdx}
      total={questions.length}
      onAnswered={record}
      onNext={next}
    />
  );
}

/* =========================================================================
 * Bloc exercice — alphabet et vocab passent par <Quiz>, les autres gardent
 * leur rendu dédié (sentence / course / review / errors)
 * ======================================================================= */
function ExerciseBlock({ item, onAnswer, onAdvance }) {
  const meta = SUBTYPE_META[item.subtype] || {
    icon: Target,
    label: item.subtype,
  };
  const Icon = meta.icon;

  // Tous les hooks en haut, jamais dans un `if` — règle React incontournable.
  const [hint, setHint] = useState(null);
  const [showLetters, setShowLetters] = useState(true);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  // Alphabet et vocab passent par le même composant Quiz que les exos
  // normaux (PracticeAlphabet / VocabQuizSession) — même génération de
  // question, même affichage, même fiche d'indice.
  const isAlphabet = item.subtype === "alphabet";
  const isVocab = item.subtype === "vocab";

  const vocabQuestions = useMemo(() => {
    if (!isVocab) return null;
    const words = item.questions || [];
    const wide = words.length >= 6 ? words : words.concat(words, words);
    return buildVocabQuestions(words, "mixed", wide);
  }, [isVocab, item.questions]);

  const wordById = useMemo(() => {
    if (!isVocab) return {};
    const m = {};
    for (const w of item.questions || []) m[w.id] = w;
    return m;
  }, [isVocab, item.questions]);

  const letterByChar = useMemo(() => {
    if (!isAlphabet) return {};
    const m = {};
    for (const q of item.questions || []) {
      m[q.char] = {
        char: q.char,
        name: q.letter_name,
        translit: q.translit,
        sound: q.translit,
        hint: q.hint,
      };
    }
    return m;
  }, [isAlphabet, item.questions]);

  // Même nettoyage que PracticeAlphabet::cleanQuestions — sans ça, Quiz.jsx
  // affiche "translit — nom" sous la lettre, ce qui donne la réponse en
  // mode "reconnaître le nom". La séquence est aussi réordonnée pour ne
  // jamais avoir deux fois la même lettre d'affilée.
  const cleanedAlphabetQuestions = useMemo(() => {
    if (!isAlphabet) return [];
    return makeStrictSequence(
      (item.questions || []).map((q) => {
        const clean = { ...q };
        delete clean.translit;
        delete clean.hint;
        return clean;
      }),
    );
  }, [isAlphabet, item.questions]);

  const finishViaQuiz = (result) => {
    for (const r of result.results || []) {
      onAnswer({
        item_key: r.key,
        item_type: item.subtype || "exercise",
        ref_id: r.ref || null,
        prompt: r.prompt,
        correct: r.correct,
        skill: r.skill || "grammar",
        difficulty: 2,
        options: r.options || [],
        answer: r.answer ?? 0,
        char: r.char || null,
        reward_mode: item.reward_mode || "classique",
      });
    }
    onAdvance();
  };

  if (!item.questions || item.questions.length === 0) {
    return (
      <div className="rounded-3xl border bg-card p-8 text-center">
        <p className="text-muted-foreground font-medium mb-6">
          {t("dailyplansession.rien_a_travailler_ici_aujourd_hui")}
        </p>
        <button
          onClick={onAdvance}
          className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold"
        >
          {t("exerciseblock.continuer")}
        </button>
      </div>
    );
  }

  if (isAlphabet) {
    const letters = Object.values(letterByChar);

    if (showLetters) {
      return (
        <AlphabetLearnScreen
          letters={letters}
          onDone={() => setShowLetters(false)}
        />
      );
    }

    return (
      <>
        <div className="flex items-center gap-4 mb-6 px-2">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={26} />
          </div>
          <div className="font-head text-2xl font-bold truncate text-foreground">
            {item.label_fr}
          </div>
        </div>
        <Quiz
          questions={cleanedAlphabetQuestions}
          record={false}
          rewardMode={item.reward_mode || "classique"}
          onHint={(q) => setHint(letterByChar[q.char] || null)}
          onDone={finishViaQuiz}
          showCelebration={false}
          title={item.label_fr}
        />
        {hint && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHint(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <LetterCard letter={hint} />
              <button
                onClick={() => setHint(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (isVocab) {
    return (
      <>
        <div className="flex items-center gap-4 mb-6 px-2">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon size={26} />
          </div>
          <div className="font-head text-2xl font-bold truncate text-foreground">
            {item.label_fr}
          </div>
        </div>
        <Quiz
          questions={vocabQuestions}
          record={false}
          rewardMode={item.reward_mode || "classique"}
          onHint={(q) => setHint(wordById[q.ref] || null)}
          onAnswer={(q, good) => {
            if (q.ref) {
              api
                .post("/progression/vocab/answer", {
                  word_id: q.ref,
                  correct: good,
                })
                .catch(() => {});
            }
          }}
          onDone={finishViaQuiz}
          showCelebration={false}
          title={item.label_fr}
        />
        {hint && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHint(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <WordCard word={hint} />
              <button
                onClick={() => setHint(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Sentence / course / review / errors : logique existante inchangée.
  const questions = item.questions || [];

  const record = (correct) => {
    const q = questions[idx];
    setAnswers((prev) => [...prev, { correct }]);
    onAnswer({
      item_key: q.key,
      item_type: item.subtype || "exercise",
      ref_id: q.ref || null,
      prompt: q.prompt || q.translation_fr || "",
      correct,
      skill: q.skill || "grammar",
      difficulty: q.difficulty || 2,
      options: q.options || [],
      answer: q.answer ?? 0,
      char: q.char || null,
      reward_mode: item.reward_mode || "classique",
    });
  };

  const next = () => {
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else setFinished(true);
  };

  if (finished) {
    const correct = answers.filter((a) => a.correct).length;
    const score = Math.round((correct / Math.max(1, answers.length)) * 100);
    return (
      <div className="rounded-3xl border bg-card p-8 shadow-sm text-center">
        <Icon size={56} className="mx-auto mb-5 text-primary" />
        <h2 className="font-head text-3xl font-bold mb-3">{item.label_fr}</h2>
        <div className="text-5xl font-head font-extrabold mb-3">{score}%</div>
        <div className="text-muted-foreground mb-8 font-medium">
          {correct} sur {answers.length}
        </div>
        <button
          onClick={onAdvance}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-md"
        >
          {t("exerciseblock.continuer")} <ArrowRight size={22} />
        </button>
      </div>
    );
  }

  const q = questions[idx];
  return (
    <div>
      <div className="flex items-center gap-4 mb-6 px-2">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon size={26} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm uppercase font-extrabold text-primary mb-1 tracking-wider">
            {meta.label}
          </div>
          <div className="font-head text-2xl font-bold truncate text-foreground">
            {item.label_fr}
          </div>
        </div>
      </div>
      {q.kind === "sentence" ? (
        <SentenceCard
          sentence={q}
          index={idx}
          total={questions.length}
          onAnswered={record}
          onNext={next}
        />
      ) : (
        <QuestionCard
          question={q}
          index={idx}
          total={questions.length}
          onAnswered={record}
          onNext={next}
        />
      )}
    </div>
  );
}

/* =========================================================================
 * Bloc question isolée (tryhard)
 * ======================================================================= */
function SingleQuestionBlock({ item, onAnswer, onAdvance }) {
  const q = item.playable;
  const record = (correct) =>
    onAnswer({
      item_key: q.key,
      item_type: item.type,
      ref_id: q.ref || null,
      prompt: q.prompt,
      correct,
      skill: q.skill || "grammar",
      difficulty: q.difficulty || 3,
      options: q.options || [],
      answer: q.answer ?? 0,
      char: q.char || null,
      reward_mode: item.reward_mode || "classique",
    });

  return (
    <div>
      <div className="flex items-center gap-4 mb-6 px-2">
        <div className="w-14 h-14 rounded-2xl bg-ocre/15 text-ocre flex items-center justify-center shrink-0">
          <Target size={26} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm uppercase font-extrabold text-ocre mb-1 tracking-wider">
            {t("dailyplansession.rappel")}
          </div>
          <div className="font-head text-2xl font-bold truncate text-foreground">
            {item.label_fr}
          </div>
        </div>
      </div>
      <QuestionCard
        question={q}
        index={0}
        total={1}
        onAnswered={record}
        onNext={onAdvance}
      />
    </div>
  );
}

/* Registre : un type d'item -> un composant. Plus de conditions en cascade. */
const RENDERERS = {
  lesson: LessonBlock,
  exercise: ExerciseBlock,
  tryhard: SingleQuestionBlock,
};

const normalizeProgress = (progress, itemCount) =>
  Array.from({ length: itemCount }, (_, index) => progress?.[index] ?? null);

/* =========================================================================
 * Séance du jour — orchestrateur
 * ======================================================================= */
export default function DailyPlanSession() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [itemsProgress, setItemsProgress] = useState([]);
  // loading | intro | playing | interRecap | finalRecap | retrying
  const [phase, setPhase] = useState("loading");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lastItemRecap, setLastItemRecap] = useState(null);
  const [finalRecap, setFinalRecap] = useState(null);
  const [retryIdx, setRetryIdx] = useState(null);
  const [isReplaying, setIsReplaying] = useState(false);

  // Une ref plutôt qu'un state : onAnswer() est appelé plusieurs fois de
  // suite juste avant onAdvance() (ex: dans finishViaQuiz), et un `useState`
  // n'aurait pas encore appliqué les mises à jour au moment où onAdvance()
  // lit les résultats (stale closure) — la ref est synchrone, donc fiable.
  const itemResultsRef = useRef([]);

  const fetchPlan = useCallback(async () => {
    try {
      const data = await fetchDailyPlan();
      setPlan(data);
      if (data.done) {
        setFinalRecap(data.recap);
        setItemsProgress(
          normalizeProgress(data.items_progress, data.recap?.items?.length || 0),
        );
        setPhase("finalRecap");
        return;
      }
      const progress = normalizeProgress(
        data.items_progress,
        data.items?.length || 0,
      );
      setItemsProgress(progress);
      const firstPending = progress.findIndex((p) => !p);
      setCurrentIdx(firstPending === -1 ? 0 : firstPending);
      setPhase("intro"); // toujours visible, "Continuer" si progression existante
    } catch {
      setPlan({ items: [], empty: true });
      setPhase("intro");
    }
  }, []);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const items = plan?.items || [];
  const runningTotal = itemsProgress.reduce(
    (s, p) => s + (p?.dinars_earned || 0),
    0,
  );

  const onAnswer = (r) => {
    itemResultsRef.current.push(r);
  };
  const onLessonReset = (keys) => {
    itemResultsRef.current = itemResultsRef.current.filter(
      (r) => !keys.includes(r.item_key),
    );
  };

  const submitCurrentItem = async () => {
    const idx = retryIdx !== null ? retryIdx : currentIdx;
    const results = itemResultsRef.current;
    itemResultsRef.current = [];

    // Never submit or render an index that is not part of the immutable plan.
    // This also recovers gracefully from a stale client response.
    if (idx < 0 || idx >= items.length) {
      const progress = normalizeProgress(itemsProgress, items.length);
      setItemsProgress(progress);
      const next = progress.findIndex((p) => !p);
      if (next === -1 && finalRecap) {
        setPhase("finalRecap");
      } else if (next !== -1) {
        setRetryIdx(null);
        setCurrentIdx(next);
        setPhase("playing");
      } else {
        await fetchPlan();
      }
      return;
    }

    const localCorrect = results.filter((r) => r.correct).length;
    const localTotal = results.length;

    // Aperçu optimiste : affiché tout de suite, sans attendre le réseau.
    // Les dinars restent "…" jusqu'à la vraie réponse serveur.
    if (retryIdx === null) {
      setLastItemRecap({
        total: localTotal,
        correct: localCorrect,
        dinars_earned: null,
        label_fr: items[idx]?.label_fr || "",
      });
      setPhase("interRecap");
    }

    try {
      const { data } = await api.post(`/daily-plan/v2/item/${idx}/submit`, {
        results,
        is_retry: retryIdx !== null,
      });
      setItemsProgress(normalizeProgress(data.items_progress, items.length));
      window.dispatchEvent(new CustomEvent("arabdex:dinar-update"));
      if (retryIdx !== null) {
        setRetryIdx(null);
        setFinalRecap(data.final_recap || finalRecap);
        setPhase("finalRecap");
        return;
      }
      if (data.all_done) {
        setFinalRecap(data.final_recap);
        setPhase("finalRecap");
      } else {
        setLastItemRecap(data.item_recap); // remplace l'aperçu par les vrais chiffres
      }
    } catch {
      itemResultsRef.current = results; // rien n'est perdu, retry possible
      setPhase("playing");
    }
  };

  const continueAfterRecap = () => {
    const progress = normalizeProgress(itemsProgress, items.length);
    setItemsProgress(progress);
    const next = progress.findIndex((p) => !p);
    if (next === -1) {
      // Tout est fait : direction le bilan, jamais un index hors limites.
      setPhase("finalRecap");
      return;
    }
    setCurrentIdx(next);
    setPhase("playing");
  };

  const startRetry = (idx) => {
    if (idx < 0 || idx >= items.length) return;
    setRetryIdx(idx);
    itemResultsRef.current = [];
    setPhase("retrying");
  };

  const handleReplayFullPlan = async () => {
    setIsReplaying(true);
    try {
      await api.delete("/daily-plan/v2/today");
      invalidateDailyPlan();
      setPlan(null);
      setFinalRecap(null);
      setItemsProgress([]);
      setCurrentIdx(0);
      await fetchPlan();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsReplaying(false);
    }
  };

  if (phase === "loading" || !plan) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground">
        {t("lessoneditor.chargement")}
      </div>
    );
  }

  if (plan.empty || items.length === 0) {
    return (
      <div className="text-center py-20 flex flex-col items-center max-w-md mx-auto">
        <Sparkles size={48} className="text-muted-foreground mb-6 opacity-30" />
        <p className="text-xl font-bold text-foreground mb-3">
          {t("dailyplansession.rien_de_prevu_aujourd_hui")}
        </p>
        <p className="text-muted-foreground mb-8">
          {(plan.notes || [])[0] ||
            "Reviens demain, ou augmente ton objectif quotidien dans les réglages."}
        </p>
        <button
          onClick={() => nav("/dashboard")}
          className="px-8 py-4 rounded-2xl bg-primary text-primary-foreground font-bold active:scale-[0.98] transition-transform"
        >
          {t("dailyplansession.retour_au_tableau_de_bord")}
        </button>
      </div>
    );
  }

  if (phase === "intro") {
    const resuming = itemsProgress.some((p) => p);
    return (
      <DailyPlanIntro
        plan={plan}
        resuming={resuming}
        onStart={() => setPhase("playing")}
      />
    );
  }

  if (phase === "interRecap" && lastItemRecap) {
    const remaining = itemsProgress.filter((p) => !p).length;
    const remainingMinutes = items
      .filter((_, i) => !itemsProgress[i])
      .reduce((s, it) => s + (it.minutes || 0), 0);
    return (
      <InterExerciseRecap
        itemRecap={lastItemRecap}
        remainingCount={remaining}
        remainingMinutes={Math.round(remainingMinutes)}
        runningTotal={runningTotal}
        onContinue={continueAfterRecap}
        onQuit={() => nav("/dashboard")}
      />
    );
  }

  if (phase === "finalRecap" && finalRecap) {
    return (
      <Bilan
        recap={finalRecap}
        onReplay={handleReplayFullPlan}
        isReplaying={isReplaying}
        nav={nav}
        onRetryItem={startRetry}
      />
    );
  }

  const activeIdx = retryIdx !== null ? retryIdx : currentIdx;
  if (activeIdx < 0 || activeIdx >= items.length) {
    return (
      <div className="max-w-md mx-auto text-center py-20 px-4">
        <p className="text-muted-foreground mb-6">
          {t("dailyplansession.synchronisation_de_ta_seance")}
        </p>
        <button
          onClick={fetchPlan}
          className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold"
        >
          {t("dailyplansession.recharger_la_seance")}
        </button>
      </div>
    );
  }
  const currentItem = items[activeIdx];
  const Renderer = RENDERERS[currentItem?.type];

  return (
    <div className="max-w-2xl mx-auto flex flex-col min-h-screen px-2 sm:px-4 pt-4 pb-12">
      <div className="mb-2 sticky top-2 z-10 bg-background/90 backdrop-blur-md pb-2 pt-2">
        <DailyPlanHeader
          items={items}
          currentIdx={activeIdx}
          itemsProgress={itemsProgress}
        />
      </div>

      <div className="flex-1">
        <div key={activeIdx + (retryIdx !== null ? "-retry" : "")}>
          {Renderer ? (
            <Renderer
              item={currentItem}
              onAnswer={onAnswer}
              onAdvance={submitCurrentItem}
              onReset={onLessonReset}
            />
          ) : (
            /* Filet de sécurité : un item inconnu ne bloque plus la séance. */
            <div className="rounded-3xl border bg-card p-8 text-center">
              <p className="text-muted-foreground mb-6">
                {t("dailyplansession.cette_etape_n_a_pas_pu")}
              </p>
              <button
                onClick={submitCurrentItem}
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold"
              >
                {t("tutorial.passer")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
 * Bilan de séance — avec détail par exercice, cliquable pour retenter
 * ======================================================================= */
function Bilan({ recap, onReplay, isReplaying, nav, onRetryItem }) {
  const mins = Math.floor((recap.duration_seconds || 0) / 60);
  const secs = (recap.duration_seconds || 0) % 60;
  const passed = !!recap.passed;
  const lessons = Object.values(recap.lesson_status || {});
  // Total réellement gagné sur cette séance : le bonus de fin de séance plus
  // tout ce qui a été crédité en cours de route (validations de leçon, mots
  // appris, groupes de vocabulaire validés).
  const grandTotal =
    (recap.dinars_earned || 0) +
    (recap.rewards || []).reduce((sum, r) => sum + (r.earned || 0), 0);

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-6 relative">
      {passed && grandTotal > 0 && <ConfettiBurst />}
      <div
        className={`rounded-3xl border bg-card p-8 text-center shadow-lg ${
          passed ? "border-green-500/30" : "border-red-500/30"
        }`}
      >
        {passed ? (
          <CheckCircle2 size={56} className="mx-auto text-green-500 mb-6" />
        ) : (
          <XCircle size={56} className="mx-auto text-red-500 mb-6" />
        )}
        <h1 className="font-head text-4xl font-extrabold mb-3 text-foreground">
          {t("dailyplansession.seance_terminee")}
        </h1>
        <div className="text-muted-foreground font-medium text-lg mb-2 capitalize">
          {recap.verdict}
        </div>
        {recap.saveFailed && (
          <p className="text-sm text-red-600 font-semibold mb-2">
            {t("dailyplansession.tes_reponses_n_ont_pas_pu")}
          </p>
        )}
        <p
          className={`text-sm font-semibold ${
            passed
              ? "text-green-600 dark:text-green-400"
              : "text-red-600 dark:text-red-400"
          }`}
        >
          {passed
            ? `Séance validée · seuil ${recap.threshold || 80} %`
            : `Pas encore validée cette fois — retente un exercice ci-dessous pour améliorer ton score, ou reviens demain.`}
        </p>

        <div className="grid grid-cols-3 gap-4 mt-8">
          <Stat value={`${recap.pct}%`} label={t("coursequestions.score")} accent />
          <Stat value={recap.correct} label={t("dailyplansession.correctes")} />
          <Stat
            value={`${mins}:${String(secs).padStart(2, "0")}`}
            label={t("dailyplansession.temps")}
          />
        </div>
      </div>

      {(recap.dinars_earned > 0 || (recap.rewards || []).length > 0) && (
        <div className="rounded-3xl border bg-card p-6 shadow-sm">
          <div className="text-center mb-5 pb-5 border-b">
            <div className="text-xs uppercase font-black tracking-wider text-muted-foreground mb-1">
              {t("dailyplansession.total_gagne_sur_cette_seance")}
            </div>
            <div className="text-5xl font-head font-extrabold text-amber-500">
              +{grandTotal} 🪙
            </div>
          </div>
          <h2 className="font-head text-sm font-bold mb-3 flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
            <Coins className="text-amber-500" size={16} /> {t("dailyplansession.detail")}
          </h2>
          <ul className="space-y-2">
            {(recap.rewards || []).map((r, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {r.kind === "group_validated"
                    ? `Groupe « ${r.title} » validé`
                    : r.kind === "word_mastered"
                      ? `Mot maîtrisé — ${r.label}`
                      : r.label}
                </span>
                <span className="font-bold text-amber-600">+{r.earned} 🪙</span>
              </li>
            ))}
            {recap.dinars_earned > 0 && (
              <li className="flex items-center justify-between text-sm border-t pt-2">
                <span className="text-muted-foreground">
                  {t("dailyplansession.seance_du_jour_terminee")}
                </span>
                <span className="font-bold text-amber-600">
                  +{recap.dinars_earned} 🪙
                </span>
              </li>
            )}
          </ul>
          <p className="text-xs text-muted-foreground mt-4">
            {t("dailyplansession.chaque_recompense_n_est_versee_qu")}
          </p>
        </div>
      )}

      {recap.items && recap.items.length > 0 && (
        <div className="rounded-3xl border bg-card p-6 shadow-sm">
          <h2 className="font-head text-sm font-bold mb-3 text-muted-foreground uppercase tracking-wide">
            {t("dailyplansession.detail_par_exercice_clique_pour_retenter")}
          </h2>
          <div className="space-y-2">
            {recap.items.map((it) => (
              <button
                key={it.index}
                onClick={() => onRetryItem(it.index)}
                className="w-full flex items-center justify-between gap-3 rounded-2xl border p-3 text-left hover:border-primary transition"
              >
                <span className="font-medium text-sm truncate">
                  {it.label_fr}
                </span>
                <span
                  className={`text-xs font-bold shrink-0 ${
                    it.errors > 0 ? "text-red-600" : "text-emerald-600"
                  }`}
                >
                  {it.errors > 0
                    ? `${it.errors} erreur${it.errors > 1 ? "s" : ""}`
                    : t("dailyplansession.parfait")}{" "}
                  {t("dailyplansession.retenter")}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {lessons.length > 0 && (
        <div className="rounded-3xl border bg-card p-6 shadow-sm">
          <h2 className="font-head text-xl font-bold mb-4 flex items-center gap-2">
            <BookOpen className="text-primary" size={22} /> {t("dailyplansession.lecon_du_jour")}
          </h2>
          {lessons.map((l, i) => (
            <div key={i} className="rounded-2xl bg-secondary/20 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">
                  {l.pct}% — {l.passed ? t("dailyplansession.reussie") : t("dailyplansession.a_refaire")}
                </span>
                <span className="text-sm text-muted-foreground">
                  {l.validations}/{l.required}
                </span>
              </div>
              <div className="flex gap-1.5">
                {Array.from({ length: l.required }).map((_, k) => (
                  <div
                    key={k}
                    className={`h-2.5 flex-1 rounded-full ${
                      k < l.validations
                        ? "bg-green-500"
                        : "bg-secondary-foreground/15"
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {l.acquired
                  ? t("dailyplansession.lecon_acquise_elle_reviendra_en_revision")
                  : l.counted
                    ? "Validation enregistrée. La prochaine compte demain."
                    : "Cette tentative ne compte pas encore — vise 80 %."}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Relances : c'est ici que l'utilisateur doit avoir envie de continuer. */}
      <div className="grid sm:grid-cols-2 gap-4">
        <button
          onClick={() => nav("/review")}
          disabled={!recap.due_reviews}
          className="rounded-3xl border-2 border-primary/30 bg-primary/5 p-6 text-left hover:border-primary transition disabled:opacity-40 disabled:hover:border-primary/30"
        >
          <RefreshCw className="text-primary mb-3" size={26} />
          <div className="font-head text-lg font-bold mb-1">
            {t("dailyplansession.revision_espacee")}
          </div>
          <div className="text-sm text-muted-foreground">
            {recap.due_reviews
              ? `${recap.due_reviews} notion(s) à revoir aujourd'hui · jusqu'à 3 🪙 par bonne réponse`
              : t("dailyplansession.rien_a_reviser_pour_l_instant")}
          </div>
        </button>
        <button
          onClick={() => nav("/my-errors")}
          disabled={!recap.open_errors}
          className="rounded-3xl border-2 border-ocre/30 bg-ocre/5 p-6 text-left hover:border-ocre transition disabled:opacity-40 disabled:hover:border-ocre/30"
        >
          <Target className="text-ocre mb-3" size={26} />
          <div className="font-head text-lg font-bold mb-1">
            {t("dailyplansession.rejouer_mes_erreurs")}
          </div>
          <div className="text-sm text-muted-foreground">
            {recap.open_errors
              ? `${recap.open_errors} erreur(s) en attente · 2 🪙 par bonne réponse`
              : t("dailyplansession.aucune_erreur_en_attente_beau_travail")}
          </div>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={onReplay}
          disabled={isReplaying}
          className="flex-1 py-5 rounded-2xl bg-secondary text-secondary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:bg-secondary/80 transition"
        >
          <RefreshCw size={20} className={isReplaying ? t("dailyplansession.animate_spin") : ""} />
          {isReplaying ? t("dailyplansession.chargement") : t("dailyplansession.refaire_la_seance")}
        </button>
        <button
          onClick={() => nav("/dashboard")}
          className="flex-1 py-5 rounded-2xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition shadow-md"
        >
          <Home size={20} /> {t("dailyplansession.terminer")}
        </button>
      </div>
    </div>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div className="rounded-2xl border bg-secondary/20 p-5">
      <div
        className={`text-4xl font-head font-extrabold ${
          accent ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground mt-2 uppercase font-black tracking-wider">
        {label}
      </div>
    </div>
  );
}
